import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { TableRow, TableCell, Button } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'

import UserPrivilegeModal from 'components/form/UserPrivilegeModal'
import SimpleTable from 'components/ui/SimpleTable'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { getUserPrivileges } from 'store/user/thunks'
import { selectLocale } from 'store/locale/slice'
import { getCurrentUTCDateTime, localizedDateTime } from 'utils/formatDate'

interface Props {
  userState: UserState
}

export default function UserPrivileges({ userState }: Props): JSX.Element {
  const intl = useIntl()
  const [privilegeModalOpen, setPrivilegeModalOpen] = useState<boolean>(false)
  const [selectedPrivilegeId, setSelectedPrivilegeId] = useState<number | null>(null)
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)

  const { user, userPrivileges, userPrivilegesStatus, status } = userState

  useEffect(() => {
    if (user.id) {
      dispatch(getUserPrivileges(user.id))
    }
  }, [dispatch, user.id])

  const columns: TableColumn[] = [
    {
      id: 'privilege',
      label: intl.formatMessage({ id: 'strings.privilege' }),
    },
    {
      id: 'validFrom',
      label: intl.formatMessage({ id: 'strings.validFrom' }),
      description: 'UTC+0',
    },
    {
      id: 'validTo',
      label: intl.formatMessage({ id: 'strings.validTo' }),
      description: 'UTC+0',
    },
    {
      id: 'updated',
      label: intl.formatMessage({ id: 'strings.updated' }),
      description: 'UTC+0',
    },
    {
      id: 'notes',
      label: intl.formatMessage({ id: 'strings.notes' }),
    },
    {
      id: 'source',
      label: intl.formatMessage({ id: 'strings.source' }),
    },
    {
      id: 'status',
      label: intl.formatMessage({ id: 'strings.status' }),
    },
    {
      id: 'edit',
      label: '',
    },
  ]

  const getDate = (date) => {
    if (date) {
      return localizedDateTime(new Date(date.date), locale.language.code)
    }
    return '-'
  }

  const isActive = (privilege: UserPrivilege) => {
    const validFrom = privilege.validFrom ? new Date(privilege.validFrom.date) : new Date()
    const validTo = privilege.validTo ? new Date(privilege.validTo.date) : new Date('2100-01-01')
    const now = new Date(getCurrentUTCDateTime())

    return now >= validFrom && now <= validTo
  }

  const togglePrivilegeModal = () => {
    const modalClosing = privilegeModalOpen
    if (modalClosing) {
      setSelectedPrivilegeId(null)
    }
    setPrivilegeModalOpen(!privilegeModalOpen)
  }

  const editPrivilege = (privilege: UserPrivilege) => {
    setSelectedPrivilegeId(privilege.id)
    togglePrivilegeModal()
  }

  const getSelectedPrivilege = () => {
    const selected = userPrivileges.filter((privilege: UserPrivilege) => privilege.id === selectedPrivilegeId)
    return selected.length > 0 ? selected[0] : null
  }

  const getSortedPrivileges = () => {
    return userPrivileges
      .slice()
      .sort((a: UserPrivilege, b: UserPrivilege) =>
        Date.parse(a.updatedAt.date) < Date.parse(b.updatedAt.date) ? 1 : -1,
      )
  }

  return (
    <>
      <NewPrivilegeButton type="button" onClick={togglePrivilegeModal}>
        {intl.formatMessage({ id: 'strings.addNew' })}
      </NewPrivilegeButton>
      <SectionTitle>{intl.formatMessage({ id: 'strings.privileges' })}</SectionTitle>

      {[userPrivilegesStatus, status].includes('loading') && userPrivileges.length === 0 ? (
        <SpinnerContainer>
          <LoadingSpinnerGreen size={72} />
        </SpinnerContainer>
      ) : (
        <>
          <SimpleTable columns={columns}>
            {getSortedPrivileges().map((privilege: UserPrivilege, i: number) => (
              <TableRow hover key={i}>
                <TableCell>{privilege.name}</TableCell>
                <TableCell>{getDate(privilege.validFrom)}</TableCell>
                <TableCell>{getDate(privilege.validTo)}</TableCell>
                <TableCell>{getDate(privilege.updatedAt)}</TableCell>
                <TableCell>{privilege.notes}</TableCell>
                <TableCell>{privilege.source ?? ''}</TableCell>
                <TableCell>
                  {isActive(privilege) ? (
                    <ActiveMembership>{intl.formatMessage({ id: 'strings.active' })}</ActiveMembership>
                  ) : (
                    <ExpiredMembership>{intl.formatMessage({ id: 'strings.expired' })}</ExpiredMembership>
                  )}
                </TableCell>
                <TableCell>
                  {privilege.name !== 'GOLD_MEMBERSHIP' && <RowEditIcon onClick={() => editPrivilege(privilege)} />}
                </TableCell>
              </TableRow>
            ))}
          </SimpleTable>

          {userPrivileges.length === 0 && <NoResults>{intl.formatMessage({ id: 'strings.noResults' })}</NoResults>}
        </>
      )}
      <UserPrivilegeModal
        open={privilegeModalOpen}
        toggle={togglePrivilegeModal}
        user={user}
        privilege={getSelectedPrivilege()}
        isActive={isActive}
      />
    </>
  )
}

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const SectionTitle = styled.h2`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const ActiveMembership = styled.span`
  color: ${(props) => props.theme.palette.primary.main};
`

const ExpiredMembership = styled.span`
  color: ${(props) => props.theme.customPalette.error};
`

const RowEditIcon = styled(EditIcon)`
  color: ${(props) => props.theme.palette.primary.main};
  cursor: pointer;
`

const NewPrivilegeButton = styled(Button)`
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.customPalette.light};
  float: right;
  margin-bottom: 10px;

  &:hover {
    background-color: ${(props) => props.theme.palette.primary.main};
    opacity: 0.85;
  }
`

const NoResults = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`
